import { useQuery } from "@tanstack/react-query";
import { PenLine } from "lucide-react";
import { create } from "zustand";
import { languageForPath, type HistoryPage } from "@ide/shared";
import { Markdown } from "@/components/markdown";
import { Dialog, DialogContent } from "@/components/ui/overlay";
import { get } from "@/lib/api";
import { useNow } from "@/lib/use-now";
import { timeAgo } from "@/lib/utils";
import { activeEditor } from "@/workspace/active-editor";
import { activeWorkspace } from "@/workspace/file-ops";
import { useWorkspace } from "@/workspace/store";
import { activePluginPath } from "./active-path";

// ───────────── Autoria (estilo GitLens) ─────────────

/** Última alteração do arquivo aberto, do histórico sem commit (quem, quando, se está editando agora). */
export function AuthorshipItem() {
  const active = useWorkspace((s) => s.active);
  const ws = activeWorkspace.current;
  const projectId = ws?.detail.project.id;
  const enabled = Boolean(active && projectId && ws?.detail.isMember);
  const { data } = useQuery({
    queryKey: ["authorship", projectId, active],
    queryFn: () =>
      get<HistoryPage>(`/projects/${projectId}/history?fileId=${encodeURIComponent(active!)}&limit=1`),
    enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const item = data?.items[0];
  if (!enabled || !item) return null;
  const who = item.authors[0];
  return (
    <button
      onClick={() => useWorkspace.getState().setPanel("history")}
      className="flex items-center gap-1.5 hover:text-fg"
      title="Ver o histórico deste arquivo"
    >
      <PenLine className="h-3 w-3" />
      {who ? who.name.split(" ")[0] : "alguém"}
      {item.authors.length > 1 ? ` +${item.authors.length - 1}` : ""} ·{" "}
      {item.live ? "editando agora" : timeAgo(item.updatedAt)}
    </button>
  );
}

// ───────────── Prévia de Markdown ─────────────

export const useMarkdownPreview = create<{ open: boolean; setOpen(open: boolean): void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

export function MarkdownPreviewOverlay() {
  const { open, setOpen } = useMarkdownPreview();
  // Atualiza enquanto a pessoa digita (o texto vem do editor aberto).
  useNow(open ? 1_000 : null);
  if (!open) return null;
  const text = activeEditor.current?.view.state.doc.toString() ?? "";
  return (
    <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
      <DialogContent wide title={`Prévia · ${activePluginPath().split("/").pop() ?? ""}`}>
        <article className="prose-kompil">
          <Markdown text={text || "_Arquivo vazio._"} />
        </article>
      </DialogContent>
    </Dialog>
  );
}

export function openMarkdownPreview(): string | null {
  const lang = languageForPath(activePluginPath());
  if (lang !== "markdown") return "Abra um arquivo .md para ver a prévia.";
  useMarkdownPreview.getState().setOpen(true);
  return null;
}
